/*
 * Runtime inline style px -> vw converter.
 * 仅转换元素 style 属性里的静态 px（不解析计算表达式）。
 * 用法：在 main.js 里 import { enableInlinePxToVw } from '@/util/inlinePx2vw'; enableInlinePxToVw();
 */

const DEFAULT_OPTIONS = {
  designWidth: 750, // 主设计稿宽度
  secondaryWidth: 375, // 可选：用于某些基于 375 的库（如 Vant）
  secondaryMatch: /^(van-|vant-)/, // 当元素 className 命中该正则时采用 secondaryWidth
  precision: 6,
  ignoreSelector: ".no-inline-vw", // 标记该选择器的元素及其子元素不转换
  attrFlag: "data-no-inline-vw", // 存在该 attribute 则跳过
  maxDepth: 6, // 避免深度递归过大
  observe: true, // 是否监听动态插入
  root: typeof document !== "undefined" ? document.body : null,
};

const pxRegex = /(-?\d*\.?\d+)px(?![a-zA-Z])/g; // 捕获数值 + px

function toFixed(num, precision) {
  const power = Math.pow(10, precision);
  return Math.round(num * power) / power;
}

function convertPxValue(px, base, precision) {
  const n = parseFloat(px);
  if (isNaN(n)) return px + "px";
  return toFixed((n / base) * 100, precision) + "vw";
}

function transformStyle(styleValue, base, precision) {
  return styleValue.replace(pxRegex, (match, num) =>
    convertPxValue(num, base, precision)
  );
}

function shouldSkip(el, options) {
  if (!el || el.nodeType !== 1) return true;
  if (el.closest(options.ignoreSelector)) return true;
  if (el.hasAttribute(options.attrFlag)) return true;
  return false;
}

function pickBase(el, options) {
  if (!options.secondaryMatch) return options.designWidth;
  const cls = el.className || "";
  if (typeof cls === "string" && options.secondaryMatch.test(cls))
    return options.secondaryWidth || options.designWidth;
  return options.designWidth;
}

function processElement(el, options, depth = 0) {
  if (depth > options.maxDepth) return;
  if (shouldSkip(el, options)) return;
  if (el.hasAttribute("data-inline-vw-done")) return; // 防止重复

  const style = el.getAttribute("style");
  if (style && pxRegex.test(style)) {
    pxRegex.lastIndex = 0; // reset
    const base = pickBase(el, options);
    const converted = transformStyle(style, base, options.precision);
    el.setAttribute("style", converted);
  }
  el.setAttribute("data-inline-vw-done", "");

  // 只处理第一层子节点，深层在 MutationObserver 中或递归受 maxDepth 限制
  const children = el.children || [];
  for (let i = 0; i < children.length; i++) {
    processElement(children[i], options, depth + 1);
  }
}

function scanRoot(options) {
  if (!options.root) return;
  processElement(options.root, options, 0);
  const nodes = options.root.querySelectorAll("*");
  nodes.forEach((n) => processElement(n, options, 0));
}

function enableInlinePxToVw(userOptions = {}) {
  if (typeof window === "undefined") return;
  const options = { ...DEFAULT_OPTIONS, ...userOptions };
  if (!options.root) options.root = document.body;

  const init = () => {
    scanRoot(options);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }

  if (options.observe && "MutationObserver" in window) {
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === "childList") {
          m.addedNodes.forEach((node) => {
            if (node.nodeType === 1) processElement(node, options, 0);
          });
        } else if (
          m.type === "attributes" &&
          m.target &&
          m.attributeName === "style"
        ) {
          processElement(m.target, options, 0);
        }
      }
    });
    observer.observe(options.root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["style"],
    });
  }
}

export { enableInlinePxToVw };
